import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { CheckCircle2, FileText, Loader2 } from "lucide-react";

export default function PortalPolicies() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [signingId, setSigningId] = useState(null);

  const load = () => api.get("/employee-portal/policies").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const sign = async (id) => {
    setSigningId(id);
    try {
      await api.post(`/employee-portal/policies/${id}/acknowledge`);
      toast({ title: "Policy signed" });
      load();
    } catch (e) {
      toast({ title: "Couldn't sign", description: e?.response?.data?.detail, variant: "destructive" });
    } finally { setSigningId(null); }
  };

  if (!data) return <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="portal-policies">
      <h1 className="text-xl font-semibold">Policies</h1>
      {!data.has_profile && (
        <Card className="border-amber-400/40 bg-amber-400/5" data-testid="portal-no-profile-warning">
          <CardContent className="p-4 text-[13px] text-amber-600 dark:text-amber-400">
            Your account isn't linked to a people profile yet, so you can't sign policies until an
            admin adds you to the people roster in UC Compliance.
          </CardContent>
        </Card>
      )}
      <div className="space-y-2">
        {data.policies.map((p) => (
          <Card key={p.id} data-testid={`portal-policy-row-${p.id}`}>
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[13px] font-medium truncate">{p.title}</div>
                  <div className="text-[12px] text-muted-foreground">{p.category} · v{p.version}</div>
                </div>
              </div>
              {p.status === "signed" ? (
                <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-500/40 bg-emerald-500/10 flex-shrink-0">
                  <CheckCircle2 className="w-3 h-3" /> Signed
                </Badge>
              ) : (
                <Button
                  size="sm"
                  disabled={!data.has_profile || signingId === p.id}
                  onClick={() => sign(p.id)}
                  data-testid={`portal-policy-sign-btn-${p.id}`}
                >
                  {signingId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Sign"}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
        {data.policies.length === 0 && <p className="text-[13px] text-muted-foreground">No policies published yet.</p>}
      </div>
    </div>
  );
}
